/**
 * 外交面板组件
 * 显示势力关系网络和外交操作
 */

import { useState } from 'react'
import { useDiplomacyStore } from '../../stores/diplomacyStore'
import { SHANG_FACTIONS } from '../../data/shangDynastyData'
import './DiplomacyPanel.css'

interface DiplomacyPanelProps {
  factionId: string
}

export function DiplomacyPanel({ factionId }: DiplomacyPanelProps) {
  const [activeTab, setActiveTab] = useState<'relations' | 'treaties' | 'marriages' | 'hostages' | 'events'>('relations')
  const [selectedFaction, setSelectedFaction] = useState<string | null>(null)
  
  const {
    getRelation,
    improveRelations,
    createTreaty
  } = useDiplomacyStore()
  
  const playerFaction = SHANG_FACTIONS.find(f => f.id === factionId)
  const otherFactions = SHANG_FACTIONS.filter(f => f.id !== factionId)
  
  // 获取与其他势力的关系
  const relations = otherFactions.map(faction => ({
    faction,
    relation: getRelation(factionId, faction.id)
  })).filter(({ relation }) => relation !== null)
  
  // 处理改善关系
  const handleImproveRelations = (targetFactionId: string) => {
    improveRelations(factionId, targetFactionId, 10)
  }
  
  // 处理提议同盟
  const handleProposeAlliance = (targetFactionId: string) => {
    createTreaty({
      type: 'alliance',
      faction1: factionId,
      faction2: targetFactionId,
      terms: {
        duration: 36, // 3年
        mutualDefense: true,
        mutualAttack: true
      },
      status: 'proposed',
      signatories: {
        faction1: 'player',
        faction2: 'ai'
      }
    })
  }
  
  if (!playerFaction) {
    return <div className="diplomacy-panel">未找到势力信息</div>
  }
  
  return (
    <div className="diplomacy-panel">
      {/* 标签页 */}
      <div className="diplomacy-tabs">
        <button 
          className={`diplomacy-tab ${activeTab === 'relations' ? 'active' : ''}`}
          onClick={() => setActiveTab('relations')}
        >
          势力关系
        </button>
        <button 
          className={`diplomacy-tab ${activeTab === 'treaties' ? 'active' : ''}`}
          onClick={() => setActiveTab('treaties')}
        >
          条约
        </button>
        <button 
          className={`diplomacy-tab ${activeTab === 'marriages' ? 'active' : ''}`}
          onClick={() => setActiveTab('marriages')}
        >
          联姻
        </button>
        <button 
          className={`diplomacy-tab ${activeTab === 'hostages' ? 'active' : ''}`}
          onClick={() => setActiveTab('hostages')}
        >
          质子
        </button>
        <button 
          className={`diplomacy-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          外交事件
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="diplomacy-content">
        {/* 势力关系 */}
        {activeTab === 'relations' && (
          <div className="relations-section">
            <div className="section-header">
              <h3>势力关系网络</h3>
              <p>与其他势力的关系状态和互动历史</p>
            </div>
            
            <div className="relations-grid">
              {relations.map(({ faction, relation }) => (
                <div 
                  key={faction.id}
                  className={`relation-card ${getRelationStatusClass(relation!.status)}`}
                  onClick={() => setSelectedFaction(faction.id)}
                >
                  <div className="faction-info">
                    <div 
                      className="faction-color"
                      style={{ backgroundColor: faction.color }}
                    />
                    <div className="faction-details">
                      <h4>{faction.name}</h4>
                      <span className="relation-status">
                        {getStatusText(relation!.status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="relation-values">
                    <div className="value-bar">
                      <label>态度</label>
                      <div className="bar-container">
                        <div 
                          className="bar" 
                          style={{ 
                            width: `${(relation!.attitude + 100) / 2}%`,
                            backgroundColor: getAttitudeColor(relation!.attitude)
                          }}
                        />
                      </div>
                      <span>{relation!.attitude}</span>
                    </div>
                    
                    <div className="value-bar">
                      <label>信任</label>
                      <div className="bar-container">
                        <div 
                          className="bar" 
                          style={{ 
                            width: `${relation!.trust}%`,
                            backgroundColor: '#3498db'
                          }}
                        />
                      </div>
                      <span>{relation!.trust}</span>
                    </div>
                    
                    <div className="value-bar">
                      <label>畏惧</label>
                      <div className="bar-container">
                        <div 
                          className="bar" 
                          style={{ 
                            width: `${relation!.fear}%`,
                            backgroundColor: '#e74c3c'
                          }}
                        />
                      </div>
                      <span>{relation!.fear}</span>
                    </div>
                    
                    <div className="value-bar">
                      <label>尊重</label>
                      <div className="bar-container">
                        <div 
                          className="bar" 
                          style={{ 
                            width: `${relation!.respect}%`,
                            backgroundColor: '#9b59b6'
                          }}
                        />
                      </div>
                      <span>{relation!.respect}</span>
                    </div>
                  </div>
                  
                  <div className="relation-actions">
                    <button 
                      className="action-btn primary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleImproveRelations(faction.id)
                      }}
                    >
                      改善关系
                    </button>
                    <button 
                      className="action-btn secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleProposeAlliance(faction.id)
                      }}
                    >
                      提议同盟
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 条约 */}
        {activeTab === 'treaties' && (
          <div className="treaties-section">
            <div className="section-header">
              <h3>外交条约</h3>
              <p>当前生效的条约和历史条约</p>
            </div>
            <div className="empty-state">
              <span className="empty-icon">📜</span>
              <p>暂无条约</p>
            </div>
          </div>
        )}
        
        {/* 联姻 */}
        {activeTab === 'marriages' && (
          <div className="marriages-section">
            <div className="section-header">
              <h3>政治联姻</h3>
              <p>通过婚姻建立的外交关系</p>
            </div>
            <div className="empty-state">
              <span className="empty-icon">💑</span>
              <p>暂无联姻</p>
            </div>
          </div>
        )}
        
        {/* 质子 */}
        {activeTab === 'hostages' && (
          <div className="hostages-section">
            <div className="section-header">
              <h3>质子交换</h3>
              <p>作为外交保证的质子</p>
            </div>
            <div className="empty-state">
              <span className="empty-icon">👑</span>
              <p>暂无质子</p>
            </div>
          </div>
        )}
        
        {/* 外交事件 */}
        {activeTab === 'events' && (
          <div className="events-section">
            <div className="section-header">
              <h3>外交事件</h3>
              <p>需要处理的外交事务</p>
            </div>
            <div className="empty-state">
              <span className="empty-icon">📰</span>
              <p>暂无外交事件</p>
            </div>
          </div>
        )}
      </div>
      
      {/* 选中势力详情 */}
      {selectedFaction && (
        <div className="faction-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>势力详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedFaction(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助函数
function getRelationStatusClass(status: string): string {
  const statusClasses = {
    alliance: 'alliance',
    friendly: 'friendly',
    neutral: 'neutral',
    hostile: 'hostile',
    at_war: 'at-war',
    vassal: 'vassal',
    suzerain: 'suzerain'
  }
  return statusClasses[status as keyof typeof statusClasses] || 'neutral'
}

function getStatusText(status: string): string {
  const statusTexts = {
    alliance: '同盟',
    friendly: '友好',
    neutral: '中立',
    hostile: '敌对',
    at_war: '交战',
    vassal: '附庸',
    suzerain: '宗主'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知'
}

function getAttitudeColor(attitude: number): string {
  if (attitude >= 70) return '#27ae60' // 友好
  if (attitude >= 30) return '#f39c12' // 中立偏友好
  if (attitude >= -30) return '#95a5a6' // 中立
  if (attitude >= -70) return '#e67e22' // 中立偏敌对
  return '#e74c3c' // 敌对
}
